<template>
  <div class="irrigation-dashboard">
    <h1>Irrigation Dashboard</h1>
    <div class="valves">
      <h2>{{ t('valve.title-valves') }}</h2>
      <div v-for="(valve, index) in valves" :key="index" class="valve">
       
        <p>{{ t('valve.name') }} {{ index + 1 }}: {{ valve.state ? t('valve.running') : t('valve.stopped') }}  <img 
            :src="valve.state ? '/src/assets/farm/irrigation-running.svg' : '/src/assets/farm/irrigation-stopped.svg'" 
            :alt="`Valve ${index + 1} is ${valve.state ? t('valve.running') : t('valve.stopped')}`" 
            class="valve-icon" 
          /></p>
        <button @click="toggleValve(index)" class="valve-button" :class="{ 'running': valve.state, 'stopped': !valve.state }">
          <el-icon>
            <CircleCloseFilled v-if="valve.state" class="close-icon" />
            <SwitchButton v-else class="open-icon" />
          </el-icon>
          {{ valve.state ? t('valve.stop') : t('valve.start') }}
        </button>
      </div>
    </div>


    <div ref="chart" class="chart"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import Swal from 'sweetalert2';
import * as echarts from 'echarts';
import { CircleCloseFilled, SwitchButton } from '@element-plus/icons-vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// State for valves (using boolean for state)
const valves = ref([
  { state: false }, // false means stopped
  { state: false },
  { state: false },
  { state: false },
]);

// Random data generation for the chart
const generateRandomData = (num: number) => {
  return Array.from({ length: num }, () => Math.floor(Math.random() * 100));
};

const chartData = ref(generateRandomData(12)); // Monthly data for 12 months

onMounted(() => {
  const chart = echarts.init(document.querySelector('.chart') as HTMLElement);
  const option = {
    title: {
      text: 'Monthly Irrigation (Liters)',
    },
    tooltip: {},
    xAxis: {
      type: 'category',
      data: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ],
    },
    yAxis: {
      type: 'value',
    },
    series: [{
      name: 'Liters',
      type: 'bar',
      data: chartData.value,
    }],
  };
  chart.setOption(option);
});

// Toggle valve state
const toggleValve = (index: number) => {
  valves.value[index].state = !valves.value[index].state; // Toggle the boolean state
  const action = valves.value[index].state ? t('valve.running') : t('valve.stopped');
  Swal.fire({
    title: `Valve ${index + 1} ${action}`,
    text: `Valve ${index + 1} has been ${action} successfully.`,
    icon: action === 'started' ? 'success' : 'info',
    confirmButtonText: 'OK'
  });
};
</script>

<style scoped>
.irrigation-dashboard {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.valves {
  margin-top: 20px;
}

.valve {
  margin: 10px 0;
  display: flex;
  align-items: center;
}

.valve-icon {
  width: 30px;
  height: 30px;
  margin-right: 10px;
}

.valve-button {
  display: flex;
  align-items: center;
  background: none;
  border: 2px solid; /* Border for the button */
  border-radius: 12px; /* Rounded borders */
  cursor: pointer;
  padding: 5px 10px; /* Padding for the button */
  margin-left: auto; /* Align to the right */
}

.valve-button.running {
  border-color: red; /* Red border for running */
  color: red; /* Red text for running */
}

.valve-button.stopped {
  border-color: green; /* Green border for stopped */
  color: green; /* Green text for stopped */
}

.close-icon {
  color: red; /* Red for stopping */
}

.open-icon {
  color: green; /* Green for starting */
}

.chart {
  height: 400px;
  margin-top: 20px;
}
</style> 